
import logo from '@/assets/hiredcoach-logo.svg';
import linkedinLogo from "@/assets/linkedin.svg";
import AuthBrandPanel from "@/components/marketing/AuthBrandPanel";
import Seo from "@/components/marketing/Seo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/lib/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(1, { message: "Password is required" }),
});

type FormValues = z.infer<typeof formSchema>;

const Login = () => {
  const { login, user } = useAuth();
  const { api } = useApiClient();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Agreements modal state
  const [showAgreements, setShowAgreements] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [privacyText, setPrivacyText] = useState<string>("");
  const [termsText, setTermsText] = useState<string>("");

  // Forgot password form state
  const [fpEmail, setFpEmail] = useState("");
  const [fpSubmitting, setFpSubmitting] = useState(false);
  const [fpSubmitted, setFpSubmitted] = useState(false);
  const [fpError, setFpError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setIsSubmitting(true);
      // Get return URL from query params, default to dashboard
      const returnUrl = searchParams.get('returnUrl') || '/dashboard';
      await login(values.email, values.password, returnUrl);
      // After login, check agreements via current user in context
      setTimeout(() => {
        const needs = !(user as any)?.agreements_v1;
        if (needs) setShowAgreements(true);
      }, 0);
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startLinkedInLogin = () => {
    const returnUrl = searchParams.get('returnUrl') || '/dashboard';
    const url = `/api/auth/linkedin/start?intent=login&return_url=${encodeURIComponent(returnUrl)}`;
    window.location.assign(url);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const p = await fetch('/policies/privacy.v1.txt');
        if (p.ok) setPrivacyText(await p.text());
      } catch { }
      try {
        const t = await fetch('/policies/user-agreement.v1.txt');
        if (t.ok) setTermsText(await t.text());
      } catch { }
    };
    load();
  }, []);

  const handleAccept = async () => {
    await api.acceptAgreementsV1(agreePrivacy, agreeTerms);
    setShowAgreements(false);
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFpError(null);
    const email = fpEmail.trim() || form.getValues("email");
    if (!email) {
      setFpError("Please enter your email address");
      return;
    }
    try {
      setFpSubmitting(true);
      await api.requestPasswordReset(email);
      setFpSubmitted(true);
    } catch (err: any) {
      console.error("Password reset request failed", err);
      // Still show success to avoid user enumeration
      setFpSubmitted(true);
    } finally {
      setFpSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Sign In | HiredCoach"
        description="Sign in to HiredCoach to continue interview prep, job search organization, and mock interview practice."
        canonicalPath="/login"
        robots="noindex,follow"
      />
      <div className="flex min-h-screen bg-interview-light">
      <div className="w-full md:w-1/2 p-8 flex flex-col justify-center">
        <div className="max-w-md mx-auto w-full space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-interview-blue">Welcome Back</h1>
          </div>

          {!showForgot ? (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6 animate-fade-in"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="your.email@example.com"
                          type="email"
                          className="interview-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="********"
                          type="password"
                          className="interview-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end -mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      const currentEmail = form.getValues("email");
                      setFpEmail(currentEmail || "");
                      setShowForgot(true);
                      setFpSubmitted(false);
                    }}
                    className="text-sm text-interview-teal hover:underline"
                  >
                    Forgot your password?
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Signing in..." : "Sign in"}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11"
                  onClick={startLinkedInLogin}
                  disabled={isSubmitting}
                >
                  <img src={linkedinLogo} alt="" aria-hidden="true" className="h-4 w-4" />
                  Continue with LinkedIn
                </Button>
              </form>
            </Form>
          ) : (
            <form onSubmit={handleForgotSubmit} className="space-y-6 animate-fade-in">
              <div className="text-left">
                <button
                  type="button"
                  onClick={() => setShowForgot(false)}
                  className="text-sm text-interview-teal hover:underline"
                >
                  ← Back to sign in
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">Email</label>
                <Input
                  placeholder="your.email@example.com"
                  type="email"
                  className="interview-input mt-1"
                  value={fpEmail}
                  onChange={(e) => setFpEmail(e.target.value)}
                  autoFocus
                />
              </div>

              {fpError && <div className="text-sm text-red-600">{fpError}</div>}

              {!fpSubmitted ? (
                <Button
                  type="submit"
                  className="w-full h-11 btn-primary"
                  disabled={fpSubmitting || !fpEmail}
                >
                  {fpSubmitting ? "Sending..." : "Send reset link"}
                </Button>
              ) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 p-4">
                  <div className="font-medium mb-1">Check your email</div>
                  <p className="text-sm">
                    If an account exists for <span className="font-semibold">{fpEmail}</span>, a password reset link has been sent. The link will expire in 1 hour.
                  </p>
                </div>
              )}
            </form>
          )}

          <div className="text-center mt-4">
            <p>
              Don't have an account?{" "}
              <Link to="/register" className="text-interview-teal hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      <AuthBrandPanel
        title="Welcome back to HiredCoach"
        body="Pick up where you left off, keep your search organized, and get ready for the next interview that actually matters."
      />


        <Dialog open={showAgreements} onOpenChange={setShowAgreements}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Accept Agreements</DialogTitle>
              <DialogDescription>To continue, please review and accept our User Agreement and Privacy Policy.</DialogDescription>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border rounded p-2">
                <div className="h-60 overflow-y-auto whitespace-pre-wrap text-xs bg-gray-50 p-2">{termsText || 'Loading...'}</div>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <Checkbox checked={agreeTerms} onCheckedChange={(v: any) => setAgreeTerms(!!v)} />
                  <span>I agree to the User Agreement</span>
                </div>
              </div>
              <div className="border rounded p-2">
                <div className="h-60 overflow-y-auto whitespace-pre-wrap text-xs bg-gray-50 p-2">{privacyText || 'Loading...'}</div>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <Checkbox checked={agreePrivacy} onCheckedChange={(v: any) => setAgreePrivacy(!!v)} />
                  <span>I agree to the Privacy Policy</span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleAccept} disabled={!agreePrivacy || !agreeTerms}>Accept and Continue</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Login;
